import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Save, Loader2, Search, AlertCircle, Edit3, X } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Btn, Input } from './shared';
import { useToast } from '../../hooks/use-toast';

export default function AdminContent() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // {key, value, jsonText}
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/content');
      setItems((res.data || []).sort((a, b) => a.key.localeCompare(b.key)));
    } catch (err) {
      toast({ title: 'Load failed', description: formatApiError(err) });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (item) => {
    setEditing({
      key: item.key,
      jsonText: JSON.stringify(item.value, null, 2),
    });
    setJsonError(null);
  };

  const save = async () => {
    setJsonError(null);
    let parsed;
    try {
      parsed = JSON.parse(editing.jsonText);
    } catch (e) {
      setJsonError('Invalid JSON: ' + e.message);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/admin/content/${editing.key}`, { key: editing.key, value: parsed });
      toast({ title: 'Saved' });
      setEditing(null);
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: formatApiError(err) });
    } finally { setSaving(false); }
  };

  const filtered = items.filter((i) => !query || i.key.toLowerCase().includes(query.toLowerCase()));

  return (
    <>
      <PageHeader
        title="Site Content Blocks"
        subtitle="Every list/section seeded from the original mock data — edit as JSON"
        action={<Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>}
      />

      <AdminCard>
        <div className="relative mb-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="content-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search content keys (HERO, SERVICES, TIMELINE, TEAM…)"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-500/30 outline-none text-white text-sm"
          />
        </div>

        {loading ? <Loading /> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((item) => {
              const preview = Array.isArray(item.value)
                ? `${item.value.length} items`
                : typeof item.value === 'object'
                  ? `${Object.keys(item.value || {}).length} keys`
                  : String(item.value).slice(0, 40);
              return (
                <button
                  key={item.key}
                  onClick={() => startEdit(item)}
                  data-testid={`content-card-${item.key}`}
                  className="text-left bg-white/5 hover:bg-white/10 border border-white/10 hover:border-violet-500/50 rounded-xl p-4 transition-all"
                >
                  <p className="font-bold text-white font-mono text-sm truncate">{item.key}</p>
                  <p className="text-xs text-slate-400 mt-1">{preview}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-[11px] text-violet-300"><Edit3 size={10} /> Edit JSON</span>
                </button>
              );
            })}
          </div>
        )}
      </AdminCard>

      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => !saving && setEditing(null)}>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <aside className="w-full max-w-3xl bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-xl font-bold text-white font-mono">{editing.key}</h3>
                <p className="text-xs text-slate-400">Edit the JSON value below — saved to MongoDB</p>
              </div>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>

            <textarea
              data-testid="content-json"
              value={editing.jsonText}
              onChange={(e) => setEditing({ ...editing, jsonText: e.target.value })}
              spellCheck={false}
              className="w-full h-[65vh] font-mono text-xs bg-slate-950 text-slate-100 p-4 rounded-xl border border-white/10 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-500/30 outline-none"
            />

            {jsonError && (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-rose-500/15 border border-rose-500/40 text-rose-100 text-xs">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{jsonError}</span>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-white/10 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Btn>
              <Btn onClick={save} disabled={saving} testId="content-save">
                {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save</>}
              </Btn>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
